/**
 * DepartmentRow Component
 * Display department in horizontal row format with stats and hover actions
 */

import { useState, useEffect } from 'react';
import { hasAdminAccess } from '../../lib/roles';
import { Link } from 'react-router-dom';
import type { Department } from '../../types';
import { departmentsService } from '../../services/departments.service';
import { useAuth } from '../../hooks/useAuth';
import { getInitials } from '../../lib/utils';

export interface DepartmentRowProps {
  department: Department;
  onEdit?: (deptId: number) => void;
  onDelete?: (deptId: number) => void;
  refreshTrigger?: number;
}

export const DepartmentRow = ({ department, onEdit, onDelete, refreshTrigger }: DepartmentRowProps) => {
  const { user } = useAuth();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);

  // Load department logo as blob with JWT authentication
  useEffect(() => {
    let mounted = true;

    setLogoUrl(null);

    const loadLogo = async () => {
      if (department.has_department_logo) {
        setLogoLoading(true);
        try {
          const blobUrl = await departmentsService.fetchDepartmentImage(department.id);
          if (blobUrl && mounted) {
            setLogoUrl(blobUrl);
          }
        } catch (error) {
          console.error(`Failed to load logo for department ${department.id}:`, error);
        } finally {
          if (mounted) {
            setLogoLoading(false);
          }
        }
      }
    };

    loadLogo();

    return () => {
      mounted = false;
    };
  }, [department.id, department.has_department_logo, refreshTrigger]);

  return (
    <div className="group relative px-6 py-4 hover:bg-surface-hover active:bg-surface-hover transition-colors" data-testid="department-row">
      {/* Full-row tap target on touch devices (no hover to reveal actions) */}
      <Link
        to={`/departments/${department.id}`}
        className="absolute inset-0 z-10 can-hover:hidden"
        tabIndex={-1}
        aria-label={`View ${department.name}`}
      />

      <div className="flex items-center justify-between">
        {/* Department Info Section */}
        <div className="flex items-center space-x-5 flex-1 min-w-0">
          {/* Department Logo */}
          <Link to={`/departments/${department.id}`} className="flex-shrink-0 relative z-20">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={department.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : logoLoading ? (
              <div className="w-16 h-16 rounded-full bg-surface-secondary flex items-center justify-center">
                <svg className="animate-spin h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center">
                <span className="text-xl font-bold text-white">
                  {getInitials(department.name.split(' ')[0], department.name.split(' ')[1] || '')}
                </span>
              </div>
            )}
          </Link>

          {/* Department Name & Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-medium text-heading truncate">
              <Link
                to={`/departments/${department.id}`}
                className="relative z-20 hover:underline hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                {department.name}
              </Link>
            </h3>

              <div className="mt-1 text-sm text-meta">
                <div className="flex flex-col space-y-1 sm:flex-row sm:items-center sm:space-x-4 sm:space-y-0">
                  <span className="flex items-center">
                    <svg className="h-4 w-4 text-blue-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {department.team_count} team{department.team_count !== 1 ? 's' : ''}
                  </span>
                  <span className="flex items-center">
                    <svg className="h-4 w-4 text-green-500 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {department.member_count} member{department.member_count !== 1 ? 's' : ''}
                  </span>
                </div>
                {department.description && (
                  <p className="mt-1 text-meta truncate max-w-md">
                    {department.description}
                  </p>
                )}
              </div>
            </div>
          </div>

        {/* Action Buttons Section (Hidden by default, shown on hover) */}
        {hasAdminAccess(user?.role) && (
          <div className="department-actions-container hidden can-hover:block">
            <div className="department-actions flex items-center space-x-2 opacity-0 translate-x-5 pointer-events-none transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto">
              {onEdit && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit(department.id);
                  }}
                  className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                  title="Edit Department"
                  aria-label="Edit department"
                >
                  <svg className="h-4 w-4 mr-1.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span className="hidden sm:inline">Edit</span>
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDelete(department.id);
                  }}
                  className="action-btn inline-flex items-center px-3 py-1.5 text-sm font-medium border border-blue-600 rounded-md bg-surface text-heading hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all"
                  title="Delete Department"
                >
                  <svg className="h-4 w-4 mr-1.5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="hidden sm:inline">Delete</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tap affordance — touch devices only (whole row is the tap target there) */}
        <div className="hidden touch:flex items-center text-meta flex-shrink-0 pl-1" aria-hidden="true">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </div>
      </div>
    </div>
  );
};
